/**
 * platform-ai-orgs-page.tsx — Platform → AI → AI theo tổ chức (`/platform/ai/orgs`).
 * Nguồn AI, số dư (đ theo giá bán ra + ≈ token theo model tham chiếu), mức dùng từng tổ chức + tổng tháng; điều chỉnh ví
 * theo đ (quy ra đơn vị nội bộ khi gửi; bắt buộc lý do, ghi nhật ký Platform), khoá / mở AI của eCDP, xem sổ cái.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Ban, Building2, Loader2, ScrollText, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  aiErrorMessage, formatDong, formatSignedDong, formatTokens, ledgerKindLabel, ledgerVnd, WALLET_STATUS_META, type WalletStatus,
} from '@/lib/ai-credits'
import {
  useAdjustOrgCredits, useBlockOrgAi, usePlatformAiOrgs, usePlatformAiSettings, usePlatformOrgLedger, type PlatformAiOrg,
} from '@/hooks/use-platform-ai'
import { adjustError, SOURCES_SUMMARY_LABEL, vndToUnits } from './platform-ai-lib'

function StatusBadge({ o }: { o: PlatformAiOrg }) {
  if (o.blocked) return <Badge variant="destructive">Đã khoá AI eCDP</Badge>
  if (o.sourcesSummary === 'byok') return <Badge variant="secondary">Chỉ key riêng</Badge>
  const meta = WALLET_STATUS_META[o.status as WalletStatus]
  return meta ? <Badge variant={meta.tone}>{meta.label}</Badge> : <Badge variant="outline">{o.status}</Badge>
}

export function PlatformAiOrgsPage() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('all')
  const { data, isLoading, isError } = usePlatformAiOrgs(q.trim(), status === 'all' ? '' : status)
  const block = useBlockOrgAi()
  const [adjusting, setAdjusting] = useState<PlatformAiOrg | null>(null)
  const [ledgerOf, setLedgerOf] = useState<PlatformAiOrg | null>(null)
  const settings = usePlatformAiSettings()
  const unitVnd = settings.data?.creditValueVnd ?? 0
  const vndOf = (units: number, vnd?: number) => vnd ?? units * unitVnd

  const toggleBlock = (o: PlatformAiOrg) => {
    const next = !o.blocked
    if (!window.confirm(next
      ? `Khoá AI của eCDP cho “${o.name}”? Nhân viên AI dùng AI của eCDP sẽ tạm dừng; Key của tôi vẫn chạy.`
      : `Mở lại AI của eCDP cho “${o.name}”?`)) return
    block.mutate({ orgId: o.orgId, blocked: next }, {
      onSuccess: () => toast.success(next ? 'Đã khoá AI của eCDP' : 'Đã mở lại AI của eCDP'),
      onError: (e) => toast.error(aiErrorMessage(e)),
    })
  }

  const columns: Column<PlatformAiOrg>[] = [
    { key: 'name', header: 'Tổ chức', cell: (o) => <span className="font-medium">{o.name}</span> },
    { key: 'plan', header: 'Gói', cell: (o) => o.planId ?? '—' },
    { key: 'src', header: 'Nguồn AI', cell: (o) => SOURCES_SUMMARY_LABEL[o.sourcesSummary] ?? o.sourcesSummary },
    {
      key: 'balance', header: 'Số dư', align: 'right',
      cell: (o) => (
        <span className="tabular-nums">
          {formatDong(vndOf(o.balance, o.balanceVnd))}
          {o.balanceTokens != null && <span className="block text-xs text-muted-foreground">≈ {formatTokens(o.balanceTokens)}</span>}
        </span>
      ),
    },
    { key: 'used', header: 'Đã dùng tháng này', align: 'right', cell: (o) => <span className="tabular-nums">{formatDong(vndOf(o.usedThisMonth, o.usedThisMonthVnd))}</span> },
    { key: 'status', header: 'Trạng thái', cell: (o) => <StatusBadge o={o} /> },
    {
      key: 'actions', header: '', align: 'right',
      cell: (o) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" onClick={() => setAdjusting(o)}><SlidersHorizontal className="h-3.5 w-3.5" /> Điều chỉnh</Button>
          <Button size="sm" variant="outline" onClick={() => setLedgerOf(o)}><ScrollText className="h-3.5 w-3.5" /> Sổ cái</Button>
          <Button size="sm" variant={o.blocked ? 'default' : 'ghost'} onClick={() => toggleBlock(o)} disabled={block.isPending}>
            <Ban className="h-3.5 w-3.5" /> {o.blocked ? 'Mở khoá' : 'Khoá'}
          </Button>
        </div>
      ),
    },
  ]

  const t = data?.totals
  return (
    <div className="space-y-4">
      <PageHeader
        title="AI theo tổ chức"
        description={`Nguồn AI, số dư ví (đ theo giá bán ra${t?.referenceModel ? `, ≈ token theo ${t.referenceModel.title}` : ''}) và mức dùng của từng tổ chức. Mọi điều chỉnh / khoá được ghi nhật ký Platform.`}
      />
      {isError ? (
        <ErrorState />
      ) : data === null ? (
        <EmptyState icon={Building2} title="Máy chủ chưa hỗ trợ AI theo tổ chức" description="Tính năng AI hai chế độ đang được triển khai phía máy chủ." />
      ) : (
        <>
          {t && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Token đã bán tháng này" value={formatDong(vndOf(t.soldThisMonth, t.soldThisMonthVnd))} hint="Giá trị nạp (đ, giá bán ra)" />
              <StatCard label="Token đã dùng tháng này" value={formatDong(vndOf(t.usedThisMonth, t.usedThisMonthVnd))} hint="Theo giá bán ra" />
              <StatCard label="Chi phí nhà cung cấp" value={`$${t.providerCostUsd.toFixed(2)}`} tone="warning" />
              <StatCard
                label="Biên gộp ước tính"
                value={t.grossMarginPercent == null ? '—' : `${Math.round(t.grossMarginPercent)}%`}
                tone={t.grossMarginPercent == null || t.grossMarginPercent >= 0 ? 'success' : 'destructive'}
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Input className="max-w-xs" placeholder="Tìm tên tổ chức…" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Mọi trạng thái</SelectItem>
                <SelectItem value="ok">Bình thường</SelectItem>
                <SelectItem value="low">Sắp hết</SelectItem>
                <SelectItem value="empty">Hết token</SelectItem>
                <SelectItem value="blocked">Đã khoá</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DataTable columns={columns} rows={data?.items ?? []} rowKey={(o) => o.orgId} loading={isLoading} emptyTitle="Không có tổ chức phù hợp" />
        </>
      )}
      <AdjustDialog org={adjusting} unitVnd={unitVnd} onClose={() => setAdjusting(null)} />
      <LedgerDialog org={ledgerOf} unitVnd={unitVnd} onClose={() => setLedgerOf(null)} />
    </div>
  )
}

/** Điều chỉnh ví theo đ (giá bán ra); gửi server theo đơn vị nội bộ = đ ÷ đơn vị tính nội bộ. */
function AdjustDialog({ org, unitVnd, onClose }: { org: PlatformAiOrg | null; unitVnd: number; onClose: () => void }) {
  const adjust = useAdjustOrgCredits()
  const [vnd, setVnd] = useState('')
  const [reason, setReason] = useState('')
  const close = () => { setVnd(''); setReason(''); onClose() }
  const err = !unitVnd ? 'Chưa tải được cài đặt token' : adjustError(vnd, reason)
  const save = () => {
    if (!org) return
    if (err) return void toast.error(err)
    adjust.mutate({ orgId: org.orgId, credits: vndToUnits(Number(vnd), unitVnd), reason: reason.trim() }, {
      onSuccess: () => { toast.success(`Đã điều chỉnh ${formatSignedDong(Number(vnd))} cho ${org.name}`); close() },
      onError: (e) => toast.error(aiErrorMessage(e)),
    })
  }
  const after = org && vnd && Number.isFinite(Number(vnd)) ? (org.balanceVnd ?? org.balance * unitVnd) + Number(vnd) : null
  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Điều chỉnh ví token — {org?.name}</DialogTitle>
          <DialogDescription>Nhập số tiền (đ, giá bán ra): dương để tặng / bù, âm để trừ. Bắt buộc ghi lý do (lưu vào sổ cái và nhật ký Platform).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Số tiền (đ)</Label>
            <Input type="number" step={1000} value={vnd} onChange={(e) => setVnd(e.target.value)} placeholder="vd: 100000 hoặc -50000" />
            {after != null && <p className="text-xs text-muted-foreground">Số dư sau điều chỉnh: {formatDong(after)}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Lý do *</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="vd: Bù token do lỗi nhà cung cấp ngày 30/09" maxLength={500} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Huỷ</Button>
          <Button onClick={save} disabled={adjust.isPending || !!err}>{adjust.isPending && <Loader2 className="animate-spin" />} Xác nhận</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LedgerDialog({ org, unitVnd, onClose }: { org: PlatformAiOrg | null; unitVnd: number; onClose: () => void }) {
  const { data, isLoading, isError } = usePlatformOrgLedger(org?.orgId ?? null)
  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sổ cái token — {org?.name}</DialogTitle>
          <DialogDescription>50 bút toán gần nhất.</DialogDescription>
        </DialogHeader>
        {isLoading ? <Loading /> : isError ? <ErrorState /> : !data ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Máy chủ chưa hỗ trợ sổ cái.</p>
        ) : (
          <DataTable
            rows={data.items}
            rowKey={(r) => r.id}
            emptyTitle="Chưa có bút toán nào"
            columns={[
              { key: 'at', header: 'Ngày', cell: (r) => <span className="text-muted-foreground">{new Date(r.createdAt).toLocaleString('vi-VN')}</span> },
              { key: 'kind', header: 'Loại', cell: (r) => ledgerKindLabel(r.kind) },
              {
                key: 'note', header: 'Nội dung',
                cell: (r) => [r.note, r.usage ? `${formatTokens(r.usage.tokensIn + r.usage.tokensOut)} thật` : null].filter(Boolean).join(' · ') || '—',
              },
              { key: 'amount', header: 'Số tiền', align: 'right', cell: (r) => formatSignedDong(ledgerVnd(r, { creditValueVnd: unitVnd }).amount) },
              { key: 'after', header: 'Số dư sau', align: 'right', cell: (r) => formatDong(ledgerVnd(r, { creditValueVnd: unitVnd }).after) },
            ]}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
